import {
  Checkbox as ChakraCheckbox,
  CheckboxGroup as ChakraCheckBoxGroup,
  Text,
  For,
  useListCollection,
  Stack,
  Span,
  Box,
  HStack,
  Grid,
  VStack,
  Wrap,
} from '@chakra-ui/react';
import React from 'react';
import { isPrimitiveList } from '../utils';
import { labelStyles } from './label';
import { InfoTip, Alert, EmptyStateList } from '.';

export interface CheckboxProps extends ChakraCheckbox.RootProps {
  icon?: React.ReactNode;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  rootRef?: React.RefObject<HTMLLabelElement | null>;
  isBorder?: boolean;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  function Checkbox(props, ref) {
    const {
      icon,
      children,
      inputProps,
      rootRef,
      isBorder = true,
      ...rest
    } = props;
    return (
      <ChakraCheckbox.Root
        ref={rootRef}
        {...(labelStyles.floating.border({ isBorder: true }) as any)}
        {...rest}
      >
        <ChakraCheckbox.HiddenInput ref={ref} {...inputProps} />
        <ChakraCheckbox.Control>
          {icon || <ChakraCheckbox.Indicator />}
        </ChakraCheckbox.Control>
        {children != null && (
          <ChakraCheckbox.Label>{children}</ChakraCheckbox.Label>
        )}
      </ChakraCheckbox.Root>
    );
  },
);

export interface CheckBoxGroupProps {
  items: any[];
  onSelect: (value: any) => void;
  isLoading?: boolean;
  keyForDisplay?: string | null;
  keyForValue?: string | null;
  multiple?: boolean;
  label?: string;
  infoTip?: string;
  getEntireItem?: boolean;
  defaultValue?: string[] | number[] | string | number;
  disabled?: boolean;
  useSelectAllOption?: boolean;
  triggerResetToDefault?: number;
  layout?: 'grid' | 'wrap' | 'stack' | 'columns';
  columns?: number; // number of columns for grid layout
  spacing?: string | number; // spacing between lines
  itemContainerProps?: React.HTMLAttributes<HTMLDivElement>;
  maxHeight?: string | number;
  showScrollbar?: boolean;
  createDescription?: null | ((item: any) => string);
  generateItemCheckboxProps?: null | ((item: any) => ChakraCheckbox.RootProps);
  disableItemCondition?: null | ((item: any) => boolean);
  [key: string]: any;
}

export const CheckBoxGroup_Inner = React.forwardRef<
  HTMLDivElement,
  CheckBoxGroupProps
>(function CheckBoxGroup(
  {
    items,
    onSelect,
    keyForDisplay = null,
    keyForValue = null,
    defaultValue = [],
    multiple = true,
    label = null,
    infoTip = null,
    getEntireItem = false,
    disabled = false,
    useSelectAllOption = true,
    triggerResetToDefault = 0,
    isLoading = false,
    layout = 'grid',
    columns,
    itemContainerProps = {},
    spacing = 0,
    maxHeight,
    showScrollbar = true,
    createDescription = null,
    generateItemCheckboxProps = null,
    disableItemCondition = null,
    isBorder = true,
    ...rest
  },
  ref,
) {
  const isPrimitive = isPrimitiveList(items);
  const triggerResetToDefaultRef = React.useRef(triggerResetToDefault);
  const triggerSelectAllRef = React.useRef(false);
  const hasNormalised = React.useRef(false);

  const { collection, set } = useListCollection({
    initialItems: items,
    itemToString: (item: any) => (isPrimitive ? item : item[keyForDisplay!]),
    itemToValue: (item) =>
      isPrimitive ? String(item) : String(item[keyForValue!]),
  });

  const {
    normalisedDefaultValue,
    isNumber,
    memoDefaultItems,
    isMissingDefaultValue,
  }: {
    normalisedDefaultValue: string[];
    isNumber: boolean;
    memoDefaultItems: any[];
    isMissingDefaultValue: boolean;
  } = React.useMemo(() => {
    const normalisedDefaultValue = normaliseDefaultValue(defaultValue);

    if (!items || !Array.isArray(items) || items.length <= 0 || isLoading) {
      set([]);
      return {
        normalisedDefaultValue: [],
        isNumber: false,
        memoDefaultItems: [],
        isMissingDefaultValue: false,
      };
    }
    const isNumber = items.some(
      (item) => typeof item[keyForValue!] === 'number',
    );

    const itemsByValue = new Map<string, any>();
    for (const item of items) {
      const keyValue = isPrimitive ? String(item) : String(item[keyForValue!]);
      itemsByValue.set(keyValue, item);
    }

    const filteredDefaults: string[] = normalisedDefaultValue.filter((val) => {
      const keyVal = isNumber ? String(Number(val)) : String(val);
      return itemsByValue.has(keyVal);
    });

    const defaultItems =
      !isPrimitive && filteredDefaults.length > 0 && items.length > 0
        ? filteredDefaults.map((val) => {
            const keyVal = isNumber ? String(Number(val)) : String(val);
            return itemsByValue.get(keyVal);
          })
        : filteredDefaults;

    set(Array.isArray(items) ? (items as any[]) : []);

    return {
      normalisedDefaultValue: filteredDefaults,
      isNumber,
      initialisedInputValue:
        filteredDefaults.length === 1 && items.length > 0
          ? !isPrimitive
            ? defaultItems[0]?.[keyForDisplay!]
            : defaultItems[0]
          : undefined,
      memoDefaultItems: Array.isArray(defaultItems)
        ? (defaultItems as any[])
        : [],
      isMissingDefaultValue:
        normalisedDefaultValue.length > 0 &&
        filteredDefaults.length > 0 &&
        normalisedDefaultValue.length !== filteredDefaults.length,
    };
  }, [items]);

  const [value, setValue] = React.useState<string[]>(normalisedDefaultValue);

  React.useEffect(() => {
    if (hasNormalised.current) return;
    if (normalisedDefaultValue.length > 0) {
      setValue(normalisedDefaultValue);
      hasNormalised.current = true;
    }
  }, [normalisedDefaultValue]);

  React.useEffect(() => {
    if (isMissingDefaultValue) {
      onSelect(
        getEntireItem
          ? multiple
            ? isNumber
              ? {
                  value: normalisedDefaultValue.map(Number),
                  items: memoDefaultItems,
                }
              : { value: normalisedDefaultValue, items: memoDefaultItems }
            : isNumber
              ? {
                  value: Number(normalisedDefaultValue[0]) ?? undefined,
                  items: memoDefaultItems[0] ?? undefined,
                }
              : {
                  value: normalisedDefaultValue[0] ?? undefined,
                  items: memoDefaultItems[0] ?? undefined,
                }
          : multiple
            ? isNumber
              ? normalisedDefaultValue.map(Number)
              : normalisedDefaultValue
            : isNumber
              ? (Number(normalisedDefaultValue[0]) ?? undefined)
              : (normalisedDefaultValue[0] ?? undefined),
      );
    }
  }, [isMissingDefaultValue]);

  // const handleOnSelect = (values: string[], items: any[]) => {
  //   setValue(values);
  //   onSelect(getEntireItem ? { value: values, items: items } : values);
  // };

  // React.useEffect(() => {
  //   set(Array.isArray(items) ? (items as any[]) : []);
  //   handleOnSelect(
  //     normalisedDefaultValue,
  //     normalisedDefaultValue
  //       .map((item) =>
  //         items.find(
  //           (item: any) =>
  //             String(isPrimitive ? item : item[keyForValue!]) === item,
  //         ),
  //       )
  //       .filter(Boolean),
  //   );
  // }, [items]);

  const allItemsSelected =
    multiple &&
    useSelectAllOption &&
    value.length > 0 &&
    items.length > 0 &&
    value.length === items.length &&
    value.every((value) =>
      items.some((item: any) =>
        isPrimitive
          ? String(item) === value
          : String(item[keyForValue!]) === value,
      ),
    )
      ? true
      : false;

  const handleOnSelect = (values: string[], items: any[]) => {
    setValue(values);
    onSelect(
      getEntireItem
        ? multiple
          ? isNumber
            ? { value: values.map(Number), items: items }
            : { value: values, items: items }
          : isNumber
            ? { value: Number(values[0]), items: items[0] }
            : { value: values[0], items: items[0] }
        : multiple
          ? isNumber
            ? values.map(Number)
            : values
          : isNumber
            ? Number(values[0])
            : values[0],
    );
  };

  const handleSelectAll = () => {
    triggerSelectAllRef.current = true;

    if (!allItemsSelected) {
      handleOnSelect(
        items.map((item: any) =>
          isPrimitive ? String(item) : String(item[keyForValue!]),
        ),
        items,
      );
    } else {
      handleOnSelect([], []);
    }
  };

  const handleOnChange = (e: any) => {
    let selectedValues = Array.isArray(e) ? e : [];
    if (!multiple) {
      selectedValues = selectedValues.slice(-1);
    }
    const selectedItems = selectedValues
      .map((value) =>
        collection.items.find(
          (item) => String(isPrimitive ? item : item[keyForValue!]) === value,
        ),
      )
      .filter(Boolean);
    if (!triggerSelectAllRef.current) {
      handleOnSelect(selectedValues, selectedItems);
    } else triggerSelectAllRef.current = false;
  };

  React.useEffect(() => {
    if (triggerResetToDefaultRef.current === triggerResetToDefault - 1) {
      handleOnSelect(
        normalisedDefaultValue as string[],
        isPrimitive
          ? normalisedDefaultValue
          : items.filter((item: any) =>
              normalisedDefaultValue.includes(item[keyForValue!]),
            ),
      );
      triggerResetToDefaultRef.current = triggerResetToDefault;
    }
  }, [triggerResetToDefault]);

  const renderCheckboxItems = () => (
    <For each={collection.items}>
      {(item) => (
        <ChakraCheckbox.Root
          key={String(isPrimitive ? item : item[keyForValue!])}
          variant="outline"
          colorPalette="gray"
          size="xs"
          value={String(isPrimitive ? item : item[keyForValue!])}
          p={1}
          borderRadius="md"
          _hover={{ bg: 'primary.fg/10' }}
          {...itemContainerProps}
          {...(generateItemCheckboxProps
            ? generateItemCheckboxProps(item)
            : {})}
        >
          <ChakraCheckbox.HiddenInput
            disabled={
              disabled ||
              (disableItemCondition ? disableItemCondition(item) : false)
            }
          />
          <ChakraCheckbox.Control>
            <ChakraCheckbox.Indicator />
          </ChakraCheckbox.Control>
          <VStack align="start" gap="1" flex="1" ml={2}>
            <ChakraCheckbox.Label
              fontSize="xs"
              fontWeight="normal"
              whiteSpace="nowrap"
            >
              {String(isPrimitive ? item : item[keyForDisplay!])}
            </ChakraCheckbox.Label>
            {createDescription && (
              <Text fontSize="xs" color="fg.subtle" textStyle="caption.muted">
                {createDescription(item)}
              </Text>
            )}
          </VStack>
        </ChakraCheckbox.Root>
      )}
    </For>
  );

  const renderLayout = () => {
    const TotalColumns = columns || items.length;
    const containerProps = {
      maxHeight,
      overflow: maxHeight ? (showScrollbar ? 'auto' : 'hidden') : 'visible',
      ...(maxHeight &&
        !showScrollbar && {
          position: 'relative',
          _after: {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '20px',
            background: 'linear-gradient(transparent, white)',
            pointerEvents: 'none',
          },
        }),
    };

    switch (layout) {
      case 'grid':
        return (
          <Grid
            templateColumns={`repeat(${TotalColumns}, 1fr)`}
            gap={spacing}
            {...containerProps}
          >
            {renderCheckboxItems()}
          </Grid>
        );
      case 'columns':
        return (
          <Grid
            templateColumns={`repeat(${TotalColumns}, 1fr)`}
            gap={spacing}
            {...containerProps}
          >
            {renderCheckboxItems()}
          </Grid>
        );
      case 'stack':
        return (
          <VStack align="stretch" gap={spacing} {...containerProps}>
            {renderCheckboxItems()}
          </VStack>
        );
      case 'wrap':
      default:
        return (
          <Wrap gap={spacing} {...containerProps}>
            {renderCheckboxItems()}
          </Wrap>
        );
    }
  };

  return (
    <ChakraCheckBoxGroup
      name={label || ''}
      ref={ref}
      disabled={disabled}
      collection={collection}
      value={value}
      multiple={multiple}
      // onInteractOutside={onBlur}
      defaultValue={normalisedDefaultValue}
      onValueChange={handleOnChange}
      position="relative"
      {...(labelStyles.floating.border({ isBorder: true }) as any)}
      {...rest}
    >
      {label && (
        <Text {...(labelStyles.floating({ isBorder: true }) as any)}>
          {label}
          {infoTip && (
            <Span pointerEvents="auto">
              <InfoTip>
                <Alert>{infoTip}</Alert>
              </InfoTip>
            </Span>
          )}
        </Text>
      )}

      {useSelectAllOption && (
        <ChakraCheckbox.Root
          size="xs"
          variant="outline"
          colorPalette="primary"
          checked={allItemsSelected}
          onCheckedChange={handleSelectAll}
          _hover={{ bg: 'primary.fg/10' }}
          p={1}
          borderRadius="md"
          mb={2}
        >
          <ChakraCheckbox.HiddenInput disabled={disabled} />
          <ChakraCheckbox.Control>
            <ChakraCheckbox.Indicator />
          </ChakraCheckbox.Control>
          <ChakraCheckbox.Label color="primary.700" fontWeight="normal">
            <HStack align="center" gap={2}>
              <Text>{allItemsSelected ? 'Deselect All' : 'Select All'}</Text>
              <Text fontSize="xs" color="primary.600">
                ({value.length}/{items.length})
              </Text>
            </HStack>
          </ChakraCheckbox.Label>
        </ChakraCheckbox.Root>
      )}

      {renderLayout()}
    </ChakraCheckBoxGroup>
  );
});

export const CheckBoxGroup = React.forwardRef<
  HTMLDivElement,
  CheckBoxGroupProps
>(function CheckBoxGroup(props, ref) {
  return Array.isArray(props.items) && props.items.length > 0 ? (
    <CheckBoxGroup_Inner ref={ref} {...props} />
  ) : (
    <EmptyStateList isLoading={props.isLoading} title="No items to display" />
  );
});

const normaliseDefaultValue = (
  defaultValue: string[] | number[] | string | number,
): string[] => {
  if (Array.isArray(defaultValue)) {
    if (defaultValue.length <= 0) return [];
    return defaultValue.map((value) => String(value));
  }
  if (!String(defaultValue)) return [];
  return [String(defaultValue)];
};
